import { useState, useEffect, useRef, useCallback } from "react";
import api, { extractApiError } from "@/lib/api";

/**
 * useGlobalSearch
 * - Debounces user input (~250ms)
 * - Only triggers when text length >= 2
 * - Calls GET /search?q={text}
 * - Returns grouped results: { vehicles, incidents, users, organizations }
 * - Handles 422 and 429 with friendly messages
 */
export function useGlobalSearch(initialQuery = "") {
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState({ vehicles: [], incidents: [], users: [], organizations: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const timerRef = useRef(null);
  const lastQueryRef = useRef("");
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setError(null);

    const q = String(query || "").trim();
    if (q.length < 2) {
      setResults({ vehicles: [], incidents: [], users: [], organizations: [] });
      setLoading(false);
      return;
    }

    timerRef.current = setTimeout(async () => {
      lastQueryRef.current = q;
      setLoading(true);
      try {
        const { data: raw } = await api.get("/search", { params: { q } });
        if (!mountedRef.current) return;
        const data = raw?.data || raw || {};

        // Ensure stale responses are ignored
        if (lastQueryRef.current !== q) return;

        // Normalize two possible response shapes:
        // 1) { vehicles, incidents, users, organizations }
        // 2) { sections: [{ type, results }] }
        const cap = (arr) => Array.isArray(arr) ? arr.slice(0, 5) : [];
        if (Array.isArray(data.sections)) {
          const mapped = { vehicles: [], incidents: [], users: [], organizations: [] };
          data.sections.forEach((s) => {
            const t = String(s.type || "").toLowerCase();
            const rows = Array.isArray(s.results) ? s.results : (Array.isArray(s.data) ? s.data : []);
            if (t === "vehicle" || t === "vehicles") mapped.vehicles = cap(rows);
            else if (t === "incident" || t === "incidents") mapped.incidents = cap(rows);
            else if (t === "user" || t === "users") mapped.users = cap(rows);
            else if (t === "organization" || t === "organizations") mapped.organizations = cap(rows);
          });
          setResults(mapped);
        } else {
          setResults({
            vehicles: cap(data.vehicles),
            incidents: cap(data.incidents),
            users: cap(data.users),
            organizations: cap(data.organizations),
          });
        }
      } catch (err) {
        if (!mountedRef.current) return;
        const apiErr = extractApiError(err);

        if (err?.response?.status === 429 || apiErr?.code === 429) {
          setError("Rate limited, please try again shortly.");
        } else if (err?.response?.status === 422 || apiErr?.code === 422) {
          setError(apiErr.message || "Invalid search query.");
        } else {
          setError(apiErr.message || "An error occurred while searching.");
        }

        setResults({ vehicles: [], incidents: [], users: [], organizations: [] });
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    }, 250);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [query]);

  const clear = useCallback(() => {
    setQuery("");
    setResults({ vehicles: [], incidents: [], users: [], organizations: [] });
    setError(null);
    setLoading(false);
  }, []);

  return { query, setQuery, results, loading, error, clear };
}

export default useGlobalSearch;
